import { Injectable, inject } from '@angular/core';
import { WorkspaceStore } from '@shared/services/workspace.store';

/**
 * Service for managing Firestore collection paths - WORKSPACE-BASED ARCHITECTURE v2.0
 * 
 * Updated to use workspace-scoped paths instead of user-scoped paths.
 * All data now belongs to workspaces, not individual users.
 * 
 * @version 2.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class FirestorePathService {
  private workspaceStore = inject(WorkspaceStore);

  /**
   * Collections that should be nested under workspaces/{workspaceId}/
   * (Updated from user-scoped to workspace-scoped in v2.0)
   */
  private readonly WORKSPACE_SCOPED_COLLECTIONS = [
    'properties',
    'property_groups',
    'tenants',
    'contracts',
    'loans',
    'expenses',
    'cash_positions',
    'cashflows',
    'maintenance',         // legacy — read-aliased; new writes go to interventions
    'maintenance_logs',
    'repairs',             // legacy — read-aliased; new writes go to interventions
    'repair_logs',
    'interventions',       // unified successor of maintenance + repairs (v3.0)
    'intervention_logs',
    'documents',
    'investmentSimulations',
    'indexationHistory',  // Saved rent-indexation calculations (Outils)
    'propertyEvaluations',
    'storage_quotas',
    'billing-profiles',
    'service_requests',
    'service_providers',
    'assignment_history',
    'property_provider_preferences',
    'deletion_logs',
    'activities',  // Activity logs for workspace
    'charges',  // Building/property charges (legacy)
    'charge_statements',  // Charge statements
    'charge_periods',  // Individual charge period records (new structure)
    'chatConversations',  // Conversational AI chatbot conversations
    'chatPendingActions',  // Mutate-tool proposals awaiting confirmation
    'inventoryReports',  // Structured move-out/move-in inventory ("état des lieux")
    'meterTransfers',  // Meter holder transfers at lease handover
    'mailDrafts',  // Outgoing mail queue — counted by the sidenav badges
    'actionCards',  // Cockpit action cards — counted by the sidenav badges
    // Real estate agency workspace collections
    'agency_profile',
    'agency_mandates',
    'agency_properties',
    'agency_leads',
    'agency_viewings',
    'agency_deals',
    'portal_connections',
    'portal_push_logs',
    'agency_reminder_log',
    'agency_reports'
  ];

  /**
   * Collections that should be nested under properties (subcollections)
   */
  private readonly PROPERTY_SUBCOLLECTIONS = [
    'keys',
    'meters'
  ];

  /**
   * Collections that should be nested under contracts (subcollections)
   */
  private readonly CONTRACT_SUBCOLLECTIONS = [
    'plannedPayments',
    'plannedIndexations',
    'documents',
    'activities'
  ];

  /**
   * Collections that remain at root level (global/shared data)
   */
  private readonly GLOBAL_COLLECTIONS = [
    'global_config',
    'contract_templates', 
    'indexation_references',
    'email_templates',
    'letter_templates',
    'predefined_value_lists'
  ];

  constructor() {}

  /**
   * Get workspace-scoped collection path (v2.0)
   * @param collectionName - Name of the collection
   * @param workspaceId - Workspace ID (defaults to current workspace)
   * @returns Full path to workspace-scoped collection
   */
  getWorkspaceCollection(collectionName: string, workspaceId?: string): string {
    const wid = workspaceId || this.getCurrentWorkspaceId();
    this.validateWorkspaceScopedCollection(collectionName);
    return `workspaces/${wid}/${collectionName}`;
  }

  /**
   * Get workspace-scoped document path (v2.0)
   * @param collectionName - Name of the collection
   * @param documentId - Document ID
   * @param workspaceId - Workspace ID (defaults to current workspace)
   * @returns Full path to workspace-scoped document
   */
  getWorkspaceDocument(collectionName: string, documentId: string, workspaceId?: string): string {
    return `${this.getWorkspaceCollection(collectionName, workspaceId)}/${documentId}`;
  }

  /**
   * Get property subcollection path (e.g., workspaces/{workspaceId}/properties/{propertyId}/keys)
   * @param propertyId - Property ID
   * @param subcollectionName - Name of the subcollection (keys, meters)
   * @param workspaceId - Workspace ID (defaults to current workspace)
   * @returns Full path to property subcollection
   */
  getPropertySubcollection(propertyId: string, subcollectionName: string, workspaceId?: string): string {
    this.validatePropertySubcollection(subcollectionName);
    const propertyPath = this.getWorkspaceDocument('properties', propertyId, workspaceId);
    return `${propertyPath}/${subcollectionName}`;
  }

  /**
   * Get property subcollection document path
   * @param propertyId - Property ID
   * @param subcollectionName - Name of the subcollection (keys, meters)
   * @param documentId - Document ID within the subcollection
   * @param workspaceId - Workspace ID (defaults to current workspace)
   * @returns Full path to property subcollection document
   */
  getPropertySubcollectionDocument(propertyId: string, subcollectionName: string, documentId: string, workspaceId?: string): string {
    return `${this.getPropertySubcollection(propertyId, subcollectionName, workspaceId)}/${documentId}`;
  }

  /**
   * Get contract subcollection path (e.g., workspaces/{workspaceId}/contracts/{contractId}/plannedPayments)
   * @param contractId - Contract ID
   * @param subcollectionName - Name of the subcollection (plannedPayments, plannedIndexations)
   * @param workspaceId - Workspace ID (defaults to current workspace)
   * @returns Full path to contract subcollection
   */
  getContractSubcollection(contractId: string, subcollectionName: string, workspaceId?: string): string {
    this.validateContractSubcollection(subcollectionName);
    const contractPath = this.getWorkspaceDocument('contracts', contractId, workspaceId);
    return `${contractPath}/${subcollectionName}`;
  }

  /**
   * Get contract subcollection document path
   * @param contractId - Contract ID
   * @param subcollectionName - Name of the subcollection (plannedPayments, plannedIndexations)
   * @param documentId - Document ID within the subcollection
   * @param workspaceId - Workspace ID (defaults to current workspace)
   * @returns Full path to contract subcollection document
   */
  getContractSubcollectionDocument(contractId: string, subcollectionName: string, documentId: string, workspaceId?: string): string {
    return `${this.getContractSubcollection(contractId, subcollectionName, workspaceId)}/${documentId}`;
  }

  /**
   * Check if collection should be workspace-scoped (v2.0)
   * @param collectionName - Collection name to check
   * @returns True if collection should be workspace-scoped
   */
  isWorkspaceScopedCollection(collectionName: string): boolean {
    return this.WORKSPACE_SCOPED_COLLECTIONS.includes(collectionName);
  }

  /**
   * Check if collection is a property subcollection
   * @param collectionName - Collection name to check
   * @returns True if collection should be nested under properties
   */
  isPropertySubcollection(collectionName: string): boolean {
    return this.PROPERTY_SUBCOLLECTIONS.includes(collectionName);
  }

  /**
   * Check if collection is global (read-only or admin-managed)
   * @param collectionName - Collection name to check
   * @returns True if collection is global
   */
  isGlobalCollection(collectionName: string): boolean {
    return this.GLOBAL_COLLECTIONS.includes(collectionName);
  }

  /**
   * Get current workspace ID (v2.0)
   * @returns Current workspace ID
   * @throws Error if workspace is not loaded
   */
  private getCurrentWorkspaceId(): string {
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) {
      throw new Error('No workspace loaded. Please ensure user is logged in and workspace is initialized.');
    }
    return workspaceId;
  }

  /**
   * Validate that a collection should be workspace-scoped (v2.0)
   * @param collectionName - Collection name to validate
   * @throws Error if collection is not workspace-scoped
   */
  private validateWorkspaceScopedCollection(collectionName: string): void {
    if (!this.isWorkspaceScopedCollection(collectionName)) {
      throw new Error(
        `Collection '${collectionName}' is not configured as workspace-scoped. ` +
        `Available workspace-scoped collections: ${this.WORKSPACE_SCOPED_COLLECTIONS.join(', ')}`
      );
    }
  }

  /**
   * Validate that a collection should be a property subcollection
   * @param subcollectionName - Subcollection name to validate
   * @throws Error if collection is not a property subcollection
   */
  private validatePropertySubcollection(subcollectionName: string): void {
    if (!this.isPropertySubcollection(subcollectionName)) {
      throw new Error(
        `Collection '${subcollectionName}' is not configured as a property subcollection. ` +
        `Available property subcollections: ${this.PROPERTY_SUBCOLLECTIONS.join(', ')}`
      );
    }
  }

  /**
   * Check if collection is a contract subcollection
   * @param collectionName - Collection name to check
   * @returns True if collection should be nested under contracts
   */
  isContractSubcollection(collectionName: string): boolean {
    return this.CONTRACT_SUBCOLLECTIONS.includes(collectionName);
  }

  /**
   * Validate that a collection should be a contract subcollection
   * @param subcollectionName - Subcollection name to validate
   * @throws Error if collection is not a contract subcollection
   */
  private validateContractSubcollection(subcollectionName: string): void {
    if (!this.isContractSubcollection(subcollectionName)) {
      throw new Error(
        `Collection '${subcollectionName}' is not configured as a contract subcollection. ` +
        `Available contract subcollections: ${this.CONTRACT_SUBCOLLECTIONS.join(', ')}`
      );
    }
  }

  /**
   * Get charges collection path
   * @param userId - User ID (kept for backward compatibility)
   * @param workspaceId - Workspace ID (defaults to current workspace)
   * @returns Full path to charges collection
   */
  getChargesCollectionPath(userId: string, workspaceId?: string): string {
    return this.getWorkspaceCollection('charges', workspaceId);
  }

  /**
   * Get charge statements collection path
   * @param userId - User ID (kept for backward compatibility)
   * @param workspaceId - Workspace ID (defaults to current workspace)
   * @returns Full path to charge statements collection
   */
  getStatementsCollectionPath(userId: string, workspaceId?: string): string {
    return this.getWorkspaceCollection('charge_statements', workspaceId);
  }

  /**
   * Get charge history subcollection path
   * @param userId - User ID (kept for backward compatibility)
   * @param workspaceId - Workspace ID
   * @param chargeUID - Charge ID
   * @returns Full path to charge history subcollection
   */
  getChargeHistoryCollectionPath(userId: string, workspaceId: string, chargeUID: string): string {
    return `${this.getWorkspaceCollection('charges', workspaceId)}/${chargeUID}/history`;
  }

  /**
   * Get charge periods collection path (new structure)
   * @param userId - User ID (kept for backward compatibility)
   * @param workspaceId - Workspace ID (defaults to current workspace)
   * @returns Full path to charge periods collection
   */
  getChargePeriodsCollectionPath(userId: string, workspaceId?: string): string {
    return this.getWorkspaceCollection('charge_periods', workspaceId);
  }
}